import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import { LinkButton, ExternalButton } from "@/components/Button";
import CrmDashboard from "@/components/mockups/CrmDashboard";
import {
  TrendingUp,
  Users,
  Contact,
  Building2,
  Target,
  GitPullRequestArrow,
  CheckSquare,
  BarChart3,
  Package,
  FileText,
  Receipt,
  LayoutDashboard,
  MousePointerClick,
  FolderKanban,
  Sprout,
  Workflow,
} from "lucide-react";

const CRM_URL = "https://kryvocrm.com/";

const CAPABILITIES = [
  { icon: TrendingUp, title: "Lead Management", desc: "Capture, organize and manage leads throughout the sales process." },
  { icon: Contact, title: "Contact & Account Management", desc: "Keep customer and business information organized in one place." },
  { icon: Target, title: "Opportunity Management", desc: "Track opportunities and sales progression from qualification to closure." },
  { icon: GitPullRequestArrow, title: "Sales Pipeline", desc: "Get a clear view of deals, stages and sales activity." },
  { icon: CheckSquare, title: "Tasks & Activities", desc: "Manage follow-ups, tasks and important sales activities." },
  { icon: BarChart3, title: "Reports & Insights", desc: "Understand sales activity and business performance through reports and dashboards." },
];

const WHY_POINTS = [
  { icon: MousePointerClick, title: "Simple to Use", desc: "Designed around practical business workflows." },
  { icon: FolderKanban, title: "Centralized Customer Information", desc: "Keep sales and customer information organized." },
  { icon: Sprout, title: "Designed for Growing Businesses", desc: "A modern CRM without unnecessary complexity." },
  { icon: Workflow, title: "Connected Sales Workflow", desc: "Manage leads, opportunities, activities and customer relationships from one platform." },
];

const MODULES = [
  { icon: TrendingUp, title: "Leads" },
  { icon: Contact, title: "Contacts" },
  { icon: Building2, title: "Accounts" },
  { icon: Target, title: "Opportunities" },
  { icon: CheckSquare, title: "Tasks" },
  { icon: Package, title: "Products" },
  { icon: FileText, title: "Quotations" },
  { icon: Receipt, title: "Invoices" },
  { icon: BarChart3, title: "Reports" },
  { icon: LayoutDashboard, title: "Dashboard" },
];

const KryvoCrm = () => (
  <>
    <Seo
      title="Kryvo CRM | Modern CRM for Growing Businesses | Kryvo Technologies"
      description="Discover Kryvo CRM, a modern CRM platform for managing leads, contacts, opportunities, sales activities and customer relationships."
    />

    {/* Hero */}
    <section className="grid-bg bg-gradient-to-b from-white via-kryvo-mist/60 to-kryvo-mist pb-16 pt-[calc(72px+4rem)] sm:pb-20" data-testid="crm-hero">
      <div className="container-x grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <Link to="/products" className="text-sm font-medium text-kryvo-green hover:text-kryvo-deep" data-testid="product-back-link">
            ← All Products
          </Link>
          <div className="mt-6">
            <span className="eyebrow" data-testid="crm-eyebrow">
              <span className="h-px w-6 bg-kryvo-lime" aria-hidden="true" />
              Kryvo CRM
            </span>
          </div>
          <h1 className="mt-4 text-balance text-4xl font-semibold text-kryvo-ink sm:text-5xl">
            Run Your Sales. Manage Your Customers. <span className="text-kryvo-green">Grow Your Business.</span>
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-kryvo-sub sm:text-lg">
            Kryvo CRM is a modern customer relationship management platform designed to help
            businesses manage leads, contacts, opportunities, sales activities and customer
            relationships in one place.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ExternalButton href={CRM_URL} variant="primary" testId="crm-hero-explore">
              Explore Kryvo CRM
            </ExternalButton>
            <LinkButton to="/contact" variant="outline" arrow={false} testId="crm-hero-talk">
              Talk to Our Team
            </LinkButton>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-kryvo-lime/25 via-white to-kryvo-green/10 sm:-inset-6" aria-hidden="true" />
            <div className="relative aspect-[4/3]">
              <CrmDashboard />
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Key Capabilities */}
    <section className="container-x py-16 sm:py-20" data-testid="crm-capabilities">
      <SectionHeader
        eyebrow="Key Capabilities"
        title="Everything You Need to Manage Your Sales"
        description="The core of Kryvo CRM — focused, practical and built around how sales actually happen."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {CAPABILITIES.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.06}>
            <div className="group h-full rounded-2xl border border-kryvo-line bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-kryvo-green/40 hover:shadow-[0_20px_45px_-22px_rgba(8,122,61,0.25)]">
              <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-kryvo-soft text-kryvo-green transition-colors duration-300 group-hover:bg-kryvo-green group-hover:text-white">
                <c.icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <h3 className="text-lg font-semibold text-kryvo-ink">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-kryvo-sub">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Why Kryvo CRM */}
    <section className="border-y border-kryvo-line bg-kryvo-mist py-16 sm:py-20" data-testid="crm-why">
      <div className="container-x">
        <SectionHeader
          eyebrow="Why Kryvo CRM"
          title="Built for Modern Business"
          description="A CRM that works the way your business does — without the weight of enterprise software."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_POINTS.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.07}>
              <div className="h-full rounded-2xl border border-kryvo-line bg-white p-6">
                <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-kryvo-soft text-kryvo-green">
                  <w.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="font-semibold text-kryvo-ink">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-kryvo-sub">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Product Modules */}
    <section className="container-x py-16 sm:py-20" data-testid="crm-modules">
      <SectionHeader
        eyebrow="Product Modules"
        title="One Platform, Every Part of Sales"
        description="The major areas of Kryvo CRM — connected in a single workspace."
      />
      <div className="mx-auto mt-12 grid max-w-5xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {MODULES.map((m, i) => (
          <Reveal key={m.title} delay={i * 0.04}>
            <div className="flex h-full flex-col items-center gap-3 rounded-2xl border border-kryvo-line bg-white p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-kryvo-green/40 hover:bg-kryvo-mist">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-kryvo-soft text-kryvo-green">
                <m.icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <h3 className="text-sm font-semibold text-kryvo-ink">{m.title}</h3>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Dedicated website CTA */}
    <section className="container-x pb-24 pt-4" data-testid="crm-dedicated-cta">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-kryvo-line bg-gradient-to-b from-white to-kryvo-mist px-6 py-16 text-center sm:px-12 sm:py-20 lg:py-24">
          <div className="animate-cta-drift pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-kryvo-lime/30 blur-3xl" aria-hidden="true" />
          <div className="animate-cta-drift pointer-events-none absolute -bottom-32 -right-16 h-96 w-96 rounded-full bg-kryvo-green/10 blur-3xl [animation-delay:-7s]" aria-hidden="true" />
          <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.05]" aria-hidden="true">
            <defs>
              <pattern id="crm-cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M40 0H0v40" fill="none" stroke="#087A3D" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#crm-cta-grid)" />
          </svg>

          <div className="relative mx-auto flex max-w-2xl flex-col items-center gap-6">
            <span className="eyebrow">
              <span className="h-px w-6 bg-kryvo-lime" aria-hidden="true" />
              Discover Kryvo CRM
            </span>
            <h2 className="text-balance text-3xl font-semibold text-kryvo-ink sm:text-4xl lg:text-5xl">
              Explore <span className="text-kryvo-green">Kryvo CRM</span>
            </h2>
            <p className="max-w-xl text-base leading-relaxed text-kryvo-sub sm:text-lg">
              Discover the complete Kryvo CRM platform, features, workflows and product capabilities
              on our dedicated website.
            </p>
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              <ExternalButton href={CRM_URL} variant="primary" testId="crm-visit-website">
                Visit Kryvo CRM
              </ExternalButton>
              <LinkButton to="/contact" variant="outline" arrow={false} testId="crm-cta-talk">
                Talk to Our Team
              </LinkButton>
            </div>
            <p className="text-xs text-kryvo-sub/80">
              kryvocrm.com — the dedicated Kryvo CRM website
            </p>
          </div>
        </div>
      </Reveal>
    </section>
  </>
);

export default KryvoCrm;
